import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { Message } from '@adornis/mails/server/communication.js';
import type { TemplateResult } from 'lit';
import { DateTime } from 'luxon';
import { Institution } from '../entities/institution.js';
import { InstitutionMailLog } from '../entities/InstitutionMailLog.js';
import { globalNotification } from '../GobalNotification.js';
import { mailer } from './communication.js';

export async function sendInstitutionEmail(options: {
  institutionID: Maybe<string>;
  email: string;
  subject: string;
  content: TemplateResult<1>;
  attachmentPaths?: string[];
  cc?: Maybe<string>;
}) {
  const { institutionID, email, subject, content, attachmentPaths, cc } = options;

  const [ccInstitution, replyTo] = await (async () => {
    if (!institutionID) return [undefined, undefined];
    const institution = await getByID(
      Institution,
      institutionID,
    )({
      settings: { replyToMailAddress: 1 },
      ccMail: 1,
    });
    const ccInstitution = institution?.ccMail ?? undefined;
    const replyTo = institution?.settings.replyToMailAddress ?? undefined;
    return [ccInstitution, replyTo];
  })();

  const recipient = new Recipient(email, cc ?? ccInstitution, undefined, replyTo);
  const message = await Message.compose({
    subject: subject,
    html: content,
    attachmentPaths,
  });

  try {
    const _mailer = await mailer;
    if (!_mailer) throw new Error('could not get mailer');
    await _mailer.sendMail(message, recipient);
    const logCollection = await getRawCollection(InstitutionMailLog._collectionName);
    await logCollection.insertOne({
      mailTemplate: options.subject,
      timestamp: DateTime.now(),
      institutionID,
      email,
    });
  } catch (err) {
    const translationKey = (err as any)?.extensions?.translationKey;
    if (translationKey === 'send_mail_rejected') {
      globalNotification.next(
        'Die E-Mail konnte nicht versendet werden, da die Empfänger oder Absender-Adresse derzeit blockiert ist.',
      );
    }
    logger.error({ email, err }, 'E-Mail Konnte nicht verschickt werden; 8373');
  }
}
